import { QueryClient, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { API_ROUTES } from "definitions/constants/routeConstants";
import { generatePath } from "react-router";
import apiClient from "services/ApiClient";
import { describeNetworkingError } from "services/ApiClient/describeNetworkingError";
import { useToast } from "@chakra-ui/react";
import { apiMeetingTemplateById } from "helpers/url/api/v2";
import { MeetingTemplate } from "./models";

import { sharedTemplateKeys } from "../SharedTemplate/hooks";
import { toCreateMeetingTemplatePayload, toUpdateMeetingTemplatePayload } from "./helpers";

export const meetingTemplateKeys = {
  all: ["meetingTemplates"] as const,
  lists: () => [...meetingTemplateKeys.all, "list"] as const,
  list: (filters: string) => [...meetingTemplateKeys.lists(), { filters }] as const,
  details: () => [...meetingTemplateKeys.all, "detail"] as const,
  detail: (id: string) => [...meetingTemplateKeys.details(), id] as const,
};

export function setNewMeetingTemplateInQueryClient(queryClient: QueryClient, newMeetingTemplate: MeetingTemplate) {
  queryClient.setQueryData(meetingTemplateKeys.detail(newMeetingTemplate.id), (oldData: MeetingTemplate | undefined) => {
    if (!oldData) {
      return newMeetingTemplate;
    }
    return {
      ...oldData,
      ...newMeetingTemplate,
    };
  });
}

export const useMeetingTemplateDetailQuery = (id: string | undefined, options = {}) => useQuery<MeetingTemplate | null>({
  queryKey: meetingTemplateKeys.detail(id as string),
  queryFn: async () => apiClient.get(apiMeetingTemplateById(id as string)),
  staleTime: 1000 * 60 * 3, // 3 minutes
  enabled: !!id && ((options as { enabled?: boolean }).enabled ?? true),
  ...options,
});

export const useMeetingTemplateListQuery = (queryString: string, options = {}) => {
  const query = queryString ? `?${queryString}` : "";
  return useQuery<MeetingTemplate[]>({
    queryKey: queryString ? meetingTemplateKeys.list(queryString) : meetingTemplateKeys.lists(),
    queryFn: async () => apiClient.get(`${generatePath(API_ROUTES.meetingTemplates.base)}${query}`),
    staleTime: 1000 * 60 * 3, // 3 minutes
    ...options,
  });
};

function useMeetingTemplateCreateRequest(mutationFn: (data: any) => Promise<MeetingTemplate>) {
  const queryClient = useQueryClient();
  const toast = useToast();
  return useMutation({
    mutationFn,
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: sharedTemplateKeys.all });
      queryClient.invalidateQueries({ queryKey: meetingTemplateKeys.all });
      setNewMeetingTemplateInQueryClient(queryClient, res);
    },
    onError: (e) => {
      console.log(e);
      toast({
        title: "Error",
        description: describeNetworkingError(e),
        status: "error",
      });
    },
  });
}

function useMeetingTemplateUpdateRequest(mutationFn: (data: any) => Promise<MeetingTemplate>) {
  const queryClient = useQueryClient();
  const toast = useToast();
  return useMutation({
    mutationFn,
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: sharedTemplateKeys.all });
      queryClient.invalidateQueries({ queryKey: meetingTemplateKeys.all });
      setNewMeetingTemplateInQueryClient(queryClient, res);
    },
    onError: (e) => {
      console.log(e);
      toast({
        title: "Error",
        description: describeNetworkingError(e),
        status: "error",
      });
    },
  });
}

export function useUpdateMeetingTemplate(meetingTemplateId: string) {
  return useMeetingTemplateUpdateRequest(
    async (formData: any): Promise<MeetingTemplate> => {
      const payload = toUpdateMeetingTemplatePayload(formData);
      return apiClient.put<MeetingTemplate>(apiMeetingTemplateById(meetingTemplateId), { meetingTemplate: payload });
    },
  );
}

export function useCreateMeetingTemplate() {
  return useMeetingTemplateCreateRequest(
    async (formData: any): Promise<MeetingTemplate> => {
      const payload = toCreateMeetingTemplatePayload(formData);
      return apiClient.post<MeetingTemplate>(API_ROUTES.meetingTemplates.base, { meetingTemplate: payload });
    },
  );
}
